import type {
  Item,
  AttackContent,
  AttackProfile,
  Background,
  BackgroundTable,
  ContentPackage,
  DamageEffectProfile,
  Skill,
  StartingCombatContent,
} from './types.ts';

/**
 * Read-only lookup over a loaded content package. The rules engine never
 * imports content directly; callers pass the values it needs.
 */
export class ContentRegistry {
  private readonly skillsById: Map<string, Skill>;
  private readonly backgroundsById: Map<string, Background>;
  private readonly itemsById: Map<string, Item>;
  private readonly pkg: ContentPackage;

  constructor(pkg: ContentPackage) {
    this.pkg = pkg;
    this.skillsById = new Map(pkg.skills.map((s) => [s.id, s]));
    if (this.skillsById.size !== pkg.skills.length) {
      throw new Error('Content package contains duplicate Skill IDs');
    }
    this.backgroundsById = new Map(pkg.backgrounds.map((b) => [b.id, b]));
    this.itemsById = new Map(pkg.items.map((i) => [i.id, i]));
    if (this.backgroundsById.size !== pkg.backgrounds.length) {
      throw new Error('Content package contains duplicate Background IDs');
    }
  }

  get rulesetVersion(): string {
    return this.pkg.rulesetVersion;
  }

  allSkills(): readonly Skill[] {
    return this.pkg.skills;
  }

  getSkill(id: string): Skill | undefined {
    return this.skillsById.get(id);
  }

  requireSkill(id: string): Skill {
    const skill = this.skillsById.get(id);
    if (!skill) throw new Error(`Unknown Skill ID: ${id}`);
    return skill;
  }

  tablesFor(crawlerType: 'human' | 'animal'): BackgroundTable[] {
    return this.pkg.backgroundTables
      .filter((t) => t.crawlerType === crawlerType)
      .sort((a, b) => a.tableNumber - b.tableNumber);
  }

  /**
   * Backgrounds on a table, in the order the book prints them.
   *
   * A background with no die face is the player's own. Those sort after the
   * printed entries, so the table still reads like the book and additions
   * appear at the end rather than shuffled through it.
   */
  /** RULE DCC-INV-002 - Items are catalogue content, referenced by stable ID. */
  getItem(id: string): Item | undefined {
    return this.itemsById.get(id);
  }

  requireItem(id: string): Item {
    const item = this.itemsById.get(id);
    if (!item) throw new Error(`No Item with id "${id}" in ${this.pkg.packageId}`);
    return item;
  }

  allItems(): Item[] {
    return [...this.pkg.items];
  }

  backgroundsForTable(tableId: string): Background[] {
    return this.pkg.backgrounds
      .filter((b) => b.tableId === tableId)
      .sort((a, b) => {
        if (a.roll === null && b.roll === null) return a.name.localeCompare(b.name);
        if (a.roll === null) return 1;
        if (b.roll === null) return -1;
        return a.roll - b.roll;
      });
  }

  getBackground(id: string): Background | undefined {
    return this.backgroundsById.get(id);
  }

  get startingCombat(): StartingCombatContent {
    return this.pkg.startingCombat;
  }

  get attackContent(): AttackContent {
    return this.pkg.attacks;
  }

  /** The attack profile for a Skill, or undefined if the Skill is not an attack. */
  getAttack(skillId: string): AttackProfile | undefined {
    return this.pkg.attacks.attacks.find((a) => a.id === skillId);
  }

  /** Damage Effects legal for a given hand-to-hand Attack Skill. */
  damageEffectsFor(skillId: string): DamageEffectProfile[] {
    return this.pkg.attacks.damageEffects.filter((e) => e.appliesToSkillIds.includes(skillId));
  }
}
